import FrameProcessorPressure from "components/Modals/PatientModal/utils/FrameProcessorPressure";
import { InsoleType } from "models/Insole";
import cv from "@techstark/opencv-js";

const fp = new FrameProcessorPressure();

function round2(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
}

function footTypeFromArchIndex(archIndex: number): string {
  if (!Number.isFinite(archIndex) || archIndex < 0) return "";
  if (archIndex <= 20) return InsoleType.PesCavus;
  if (archIndex < 26) return InsoleType.Standard;
  if (archIndex < 28) return InsoleType.PesPlanusMild;
  if (archIndex < 30) return InsoleType.PesPlanusModerate;
  if (archIndex < 34) return InsoleType.PesPlanusSevere;
  return InsoleType.PesPlanusVerySevere;
}

function matOk(mat: any): boolean {
  return Boolean(mat && typeof mat.rows === "number" && mat.rows > 0 && mat.cols > 0);
}

function rectOk(roi: any): boolean {
  return Boolean(roi && Number(roi.width) > 0 && Number(roi.height) > 0);
}

function zonePeaks(pressureData: any, foot: "left" | "right") {
  const idx = foot === "left" ? 1 : 0;
  const pick = (arr: any) => {
    if (!Array.isArray(arr) || arr[idx] == null) return undefined;
    const n = Number(arr[idx]);
    return Number.isFinite(n) ? round2(n) : undefined;
  };
  const peaks = {
    m1: pick(pressureData.M1P),
    m2: pick(pressureData.M2P),
    m3: pick(pressureData.M3P),
    m4: pick(pressureData.M4P),
    m5: pick(pressureData.M5P),
  };
  return Object.values(peaks).some((v) => v != null) ? peaks : undefined;
}

function measureFoot(pressureData: any, side: "left" | "right") {
  const frame = side === "left" ? pressureData.CalibMaxFrame_Left : pressureData.CalibMaxFrame_Right;
  const roi = side === "left" ? pressureData.ROI_Left : pressureData.ROI_Right;
  if (!matOk(frame) || !rectOk(roi)) return null;

  const rect = new cv.Rect(roi.x, roi.y, roi.width, roi.height);
  const cop = fp.ComputeCOP(frame, rect);
  const upRect = new cv.Rect(rect.x, rect.y, rect.width, Math.max(1, cop.y - rect.y));
  const downRect = new cv.Rect(
    rect.x,
    cop.y + 1,
    rect.width,
    Math.max(1, rect.y + rect.height - cop.y)
  );
  const all = fp.GetAllROISensors(rect);
  const up = fp.GetAllROISensors(upRect);
  const down = fp.GetAllROISensors(downRect);
  const total = fp.GetSumPressure(all, frame);
  const fore = fp.GetSumPressure(up, frame);
  const rear = fp.GetSumPressure(down, frame);
  const split = fore + rear || 1;
  const axis = side === "left" ? pressureData.Axis_Left : pressureData.Axis_Right;
  const widthLine = side === "left" ? pressureData.WidthLine_Left : pressureData.WidthLine_Right;
  const heelLine = side === "left" ? pressureData.HeelLine_Left : pressureData.HeelLine_Right;
  const lengthCm = axis?.start && axis?.end ? round2(fp.getRealDistance(axis.start, axis.end) / 10) : undefined;
  const widthCm = widthLine?.start && widthLine?.end ? round2(fp.getRealDistance(widthLine.start, widthLine.end) / 10) : undefined;
  const heelWidthCm = heelLine?.start && heelLine?.end ? round2(fp.getRealDistance(heelLine.start, heelLine.end) / 10) : undefined;

  let archIndex = side === "left" ? Number(pressureData.FootIndex_Left) : Number(pressureData.FootIndex_Right);
  const zones = side === "left" ? pressureData.Zones_Left : pressureData.Zones_Right;
  try {
    if (zones?.GetForeFootZoneSensors && zones?.GetMidFootZoneSensors && zones?.GetRearFootZoneSensors) {
      const foreS = zones.GetForeFootZoneSensors(rect);
      const midS = zones.GetMidFootZoneSensors(rect);
      const rearS = zones.GetRearFootZoneSensors(rect);
      const midSurf = fp.GetSurface(midS, frame);
      const totalSurf = fp.GetSurface(foreS, frame) + midSurf + fp.GetSurface(rearS, frame);
      if (totalSurf > 0) archIndex = Math.round((midSurf / totalSurf) * 100);
    }
  } catch {
    // Keep stored arch index if zone reconstruction is incomplete.
  }

  const row: Record<string, unknown> = {
    pressureSharePct: round2(total),
    foreFootPct: round2((fore / split) * 100),
    rearFootPct: round2((rear / split) * 100),
    surfaceCm2: round2(fp.GetSurface(all, frame)),
    totalAvg: round2(fp.GetAveragePressure(all, frame)),
    foreFootAvg: round2(fp.GetAveragePressure(up, frame)),
    rearFootAvg: round2(fp.GetAveragePressure(down, frame)),
    archIndex: Number.isFinite(archIndex) && archIndex >= 0 ? archIndex : undefined,
    footType: footTypeFromArchIndex(archIndex),
    footLengthCm: lengthCm,
    footWidthCm: widthCm,
    heelWidthCm: heelWidthCm,
  };
  const peaks = zonePeaks(pressureData, side);
  if (peaks) row.metPeaks = peaks;
  return { row, total };
}

function addDynamicImpulse(pressureData: any, side: "left" | "right", row: Record<string, unknown>) {
  const frame = side === "left" ? pressureData.CalibMaxFrame_Left : pressureData.CalibMaxFrame_Right;
  const roi = side === "left" ? pressureData.ROI_Left : pressureData.ROI_Right;
  const zones = side === "left" ? pressureData.Zones_Left : pressureData.Zones_Right;
  if (!matOk(frame) || !rectOk(roi) || !zones?.GetForeFootZoneSensors) return;
  try {
    const rect = new cv.Rect(roi.x, roi.y, roi.width, roi.height);
    const foreS = zones.GetForeFootZoneSensors(rect);
    const midS = zones.GetMidFootZoneSensors(rect);
    const rearS = zones.GetRearFootZoneSensors(rect);
    const foreI = fp.GetSumPressure(foreS, frame);
    const midI = fp.GetSumPressure(midS, frame);
    const rearI = fp.GetSumPressure(rearS, frame);
    const totalI = foreI + midI + rearI || 1;
    const foreSurf = fp.GetSurface(foreS, frame);
    const midSurf = fp.GetSurface(midS, frame);
    const rearSurf = fp.GetSurface(rearS, frame);
    const totalSurf = foreSurf + midSurf + rearSurf || 1;
    row.foreFootImpulsePct = round2((foreI / totalI) * 100);
    row.midFootImpulsePct = round2((midI / totalI) * 100);
    row.rearFootImpulsePct = round2((rearI / totalI) * 100);
    row.foreFootPct = round2((foreSurf / totalSurf) * 100);
    row.midFootPct = round2((midSurf / totalSurf) * 100);
    row.rearFootPct = round2((rearSurf / totalSurf) * 100);
  } catch {
    // Static COP split already filled the row.
  }
}

export function buildPressureReport(pressureData: any): Record<string, unknown> | null {
  if (!pressureData) return null;
  try {
    fp.sensorWidth = Number(pressureData.PressureSensorWidth) || fp.sensorWidth;
    fp.sensorHeight = Number(pressureData.PressureSensorHeight) || fp.sensorHeight;
    const leftPack = measureFoot(pressureData, "left");
    const rightPack = measureFoot(pressureData, "right");
    if (!leftPack && !rightPack) return { available: true, examType: pressureData.ExamType || "static", note: "Pressure exists but measurements could not be computed." };
    const leftTotal = leftPack?.total || 0;
    const rightTotal = rightPack?.total || 0;
    const both = leftTotal + rightTotal || 1;
    if (leftPack) leftPack.row.pressureSharePct = round2((leftTotal / both) * 100);
    if (rightPack) rightPack.row.pressureSharePct = round2((rightTotal / both) * 100);
    const examType = String(pressureData.ExamType || "static").toLowerCase() === "dynamic" ? "dynamic" : "static";
    if (examType === "dynamic") {
      if (leftPack) addDynamicImpulse(pressureData, "left", leftPack.row);
      if (rightPack) addDynamicImpulse(pressureData, "right", rightPack.row);
    }
    return {
      available: true,
      examType,
      units: { pressure: "kg/cm2", surface: "cm2", length: "cm", share: "percent" },
      left: leftPack?.row || null,
      right: rightPack?.row || null,
    };
  } catch (error) {
    console.error("buildPressureReport failed", error);
    return { available: true, examType: pressureData.ExamType || "static", error: "Could not compute pressure measurements." };
  }
}
